import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Grid,
  Divider,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

const defaultObs = {
  rr: '',
  spo2: '',
  sbp: '',
  pulse: '',
  temp: '',
  avpu: '',
  air: true,
  o2: '',
  device: '',
  datetime: '',
  time_offset: 0,
  time_offset_unit: 'minutes',
};

export default function FacilitatorObsInput({ data = [], onChange }) {
  const API_BASE = process.env.REACT_APP_API_URL;
  const generatedReviewRef = useRef(null);

  const [obs, setObs] = useState({ ...defaultObs });
  const [datetime, setDatetime] = useState(dayjs());

  const [generating, setGenerating] = useState(false);
  const [gensummary, setGenSummary] = useState('');
  const [genHours, setGenHours] = useState('');
  const [genOffset, setGenOffset] = useState('');
  const [genOffsetUnits, setGenOffsetUnits] = useState('hours');

  const [dateoffsetunits, setDateOffsetUnits] = useState('hours');
  const [dateoffset, setDateOffset] = useState(0);

  const [generatedObs, setGeneratedObs] = useState([]);

  const handleChange = (field, value) => {
    setObs((prev) => ({ ...prev, [field]: value }));
  };

  const normaliseGeneratedObs = (o) => {
    const o2num = Number(o.o2);
    const hasOxygen = Number.isFinite(o2num)
      ? o2num > 0
      : !!String(o.o2 ?? '').trim();

    return {
      rr: o.rr ?? '',
      spo2: o.spo2 ?? '',
      sbp: o.sbp ?? '',
      pulse: o.pulse ?? '',
      temp: o.temp ?? '',
      avpu: o.avpu ?? '',
      air: typeof o.air === 'boolean' ? o.air : !hasOxygen,
      o2: o.o2 ?? '',
      device: o.device ?? (hasOxygen ? '' : 'air'),
      datetime: dayjs(o.datetime).isValid()
        ? dayjs(o.datetime).toISOString()
        : dayjs().toISOString(),
      time_offset: o.time_offset ?? 0,
      time_offset_unit: o.time_offset_unit ?? 'minutes',
    };
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);

      const res = await fetch(`${API_BASE}/api/generateobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gensummary,
          firsttime: genOffset,
          firsttimeunits: genOffsetUnits,
          timeframe: genHours,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const payload = await res.json();

      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.observations)
        ? payload.observations
        : Array.isArray(payload?.response)
        ? payload.response
        : [];

      const mapped = list.map(normaliseGeneratedObs);
      setGeneratedObs(mapped);

      setTimeout(() => {
        generatedReviewRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    } catch (err) {
      console.error('Generate obs failed:', err);
      alert('Failed to generate observations.');
    } finally {
      setGenerating(false);
    }
  };

  const updateGeneratedObs = (index, field, value) => {
    setGeneratedObs((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const deleteGeneratedObs = (index) => {
    setGeneratedObs((prev) => prev.filter((_, i) => i !== index));
  };

  const saveGeneratedObs = () => {
    if (generatedObs.length === 0) return;

    onChange?.([...data, ...generatedObs]);
    setGeneratedObs([]);
  };

  const handleSaveManual = () => {
    const newEntry = {
      ...obs,
      air: Number(obs.o2) > 0 ? false : !!obs.air,
      datetime: datetime?.toISOString?.() || dayjs().toISOString(),
      time_offset: dateoffset,
      time_offset_unit: dateoffsetunits,
    };

    onChange?.([...data, newEntry]);

    setObs({ ...defaultObs });
    setDatetime(dayjs());
    setDateOffset(0);
    setDateOffsetUnits('hours');
  };

  const renderObsEditor = (entry, index) => (
    <Paper
      key={index}
      elevation={0}
      sx={{
        border: '1px solid #d1d5db',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #d1d5db',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          Generated observation set {index + 1}
        </Typography>

        <IconButton size="small" onClick={() => deleteGeneratedObs(index)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              Observations
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="RR"
                  value={entry.rr}
                  onChange={(e) => updateGeneratedObs(index, 'rr', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="SpO₂ (%)"
                  value={entry.spo2}
                  onChange={(e) => updateGeneratedObs(index, 'spo2', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="O₂ Flow"
                  value={entry.o2}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateGeneratedObs(index, 'o2', value);
                    updateGeneratedObs(index, 'air', Number(value) > 0 ? false : true);
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="O₂ Device"
                  value={entry.device}
                  onChange={(e) => updateGeneratedObs(index, 'device', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="SBP"
                  value={entry.sbp}
                  onChange={(e) => updateGeneratedObs(index, 'sbp', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pulse"
                  value={entry.pulse}
                  onChange={(e) => updateGeneratedObs(index, 'pulse', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Temp"
                  value={entry.temp}
                  onChange={(e) => updateGeneratedObs(index, 'temp', e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="AVPU"
                  value={entry.avpu}
                  onChange={(e) => updateGeneratedObs(index, 'avpu', e.target.value)}
                >
                  {['Alert', 'V', 'P', 'U'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: 1.5,
                bgcolor: '#fafafa',
                height: '100%',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                Timing
              </Typography>

              <Stack spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Time offset"
                  value={entry.time_offset}
                  onChange={(e) => updateGeneratedObs(index, 'time_offset', e.target.value)}
                />

                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Offset unit"
                  value={entry.time_offset_unit}
                  onChange={(e) => updateGeneratedObs(index, 'time_offset_unit', e.target.value)}
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                </TextField>

                <Typography variant="caption" color="text.secondary">
                  This controls when the observation appears relative to the scenario time.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MonitorHeartOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Observations</Typography>
            <Typography variant="body2" color="text.secondary">
              Add manual observations or generate a progression for the scenario.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #d1d5db',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid #d1d5db',
                  bgcolor: '#f8fafc',
                }}
              >
                <Typography sx={{ fontWeight: 700, marginBottom:0.5 }}>
                  Generate observations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto generate observations based on the provided summary and time frame.
                </Typography>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  label="Clinical summary"
                  placeholder='e.g. "Worsening respiratory parameters with signs of septic shock."'
                  value={gensummary}
                  onChange={(e) => setGenSummary(e.target.value)}
                />

                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Stack sx={{display:'flex', flexDirection:'row', gap:1}}>
                    <Tooltip
                      title="Sets the timeframe of the observations. E.g. Starting 24 hours ago, spread across 6 hours"
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18, cursor: 'help', color: 'text.secondary' }} />
                    </Tooltip>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                      Timeframe
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      type="number"
                      label="Start time (offset)"
                      value={genOffset}
                      sx={{ minWidth: 100 }}
                      onChange={(e) => setGenOffset(e.target.value)}
                    />

                    <TextField
                      select
                      size="small"
                      label="Offset units"
                      value={genOffsetUnits}
                      sx={{ minWidth: 100 }}
                      onChange={(e) => setGenOffsetUnits(e.target.value)}
                    >
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                    </TextField>

                    <TextField
                      size="small"
                      label="Timeframe (hrs)"
                      value={genHours}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^\d*$/.test(v)) setGenHours(v);
                      }}
                      sx={{ minWidth: 100 }}
                    />
                  </Stack>
                </Paper>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={generating}
                    sx={{ textTransform: 'none', minWidth: 190 }}
                  >
                    Generate observations
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: '1px solid #d1d5db',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid #d1d5db',
                  bgcolor: '#f8fafc',
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Manual entry</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add a single observation set manually.
                </Typography>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="RR"
                      type="number"
                      value={obs.rr}
                      onChange={(e) => handleChange('rr', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Sats (%)"
                      type="number"
                      value={obs.spo2}
                      onChange={(e) => handleChange('spo2', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="O₂ Flow"
                      value={obs.o2}
                      onChange={(e) => handleChange('o2', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="O₂ Device"
                      value={obs.device}
                      onChange={(e) => handleChange('device', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="SBP"
                      type="number"
                      value={obs.sbp}
                      onChange={(e) => handleChange('sbp', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pulse"
                      type="number"
                      value={obs.pulse}
                      onChange={(e) => handleChange('pulse', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Temp"
                      type="number"
                      value={obs.temp}
                      onChange={(e) => handleChange('temp', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      sx={{minWidth:'130px'}}
                      size="small"
                      label="AVPU"
                      value={obs.avpu}
                      onChange={(e) => handleChange('avpu', e.target.value)}
                    >
                      {['Alert', 'V', 'P', 'U'].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="Time offset"
                      type="number"
                      value={dateoffset}
                      onChange={(e) => setDateOffset(e.target.value)}
                      sx={{ width: 180 }}
                    />

                    <TextField
                      size="small"
                      select
                      label="Offset unit"
                      value={dateoffsetunits}
                      onChange={(e) => setDateOffsetUnits(e.target.value)}
                      sx={{ width: 180 }}
                    >
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                    </TextField>

                    <Button
                      variant="contained"
                      onClick={handleSaveManual}
                      sx={{ textTransform: 'none', minWidth: 110 }}
                      color="success"
                    >
                      Save
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Paper>
          </Box>

          {generatedObs.length > 0 && (
            <Paper
              ref={generatedReviewRef}
              elevation={0}
              sx={{
                mt: 3,
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: ACCENT,
                  borderBottom: `1px solid ${ACCENT_BORDER}`,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  Review generated observations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit any generated values before saving.
                </Typography>
              </Box>

<Box sx={{ p: 2 }}>
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(3, minmax(0, 1fr))',
      },
      gap: 1.5,
    }}
  >
    {generatedObs.map((entry, index) => renderObsEditor(entry, index))}
  </Box>

  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
    <Button
      variant="outlined"
      color="inherit"
      onClick={() => setGeneratedObs([])}
      sx={{ textTransform: 'none' }}
    >
      Clear generated observations
    </Button>

    <Button
      variant="contained"
      color="success"
      onClick={saveGeneratedObs}
      sx={{ textTransform: 'none' }}
    >
      Save generated observations
    </Button>
  </Box>
</Box>
            </Paper>
          )}
        </Box>
      </Paper>

      <Modal open={generating}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            border: `1px solid ${ACCENT_BORDER}`,
            borderRadius: 3,
            boxShadow: 24,
            px: 4,
            py: 3,
            minWidth: 280,
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography sx={{ fontWeight: 700 }}>
            Generating observations
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
}